import {
  addDoc,
  collection,
  deleteDoc,
  onSnapshot,
  query,
  setDoc,
  doc,
  getDoc,
  QueryFieldFilterConstraint,
  QueryOrderByConstraint,
  QueryStartAtConstraint,
  QueryLimitConstraint,
} from 'firebase/firestore'

import { FirebaseCollection } from '@/constants/firebase-collection'
import { firestore } from '@/services/firebase'
import { delay } from '@/utils/tricks'

interface GetListProps {
  companyId: string
  setSupplierList: (stockList: Supplier[] | undefined) => void
  setLoadingList: React.Dispatch<React.SetStateAction<boolean>>
  whereList?: QueryFieldFilterConstraint[]
  orderByList?: QueryOrderByConstraint[]
  startAfter?: QueryStartAtConstraint[]
  limit?: QueryLimitConstraint[]
  timeDelay?: number
}
export const getList = async ({
  companyId,
  setSupplierList,
  setLoadingList,
  whereList,
  orderByList,
  startAfter,
  limit,
  timeDelay,
}: GetListProps) => {
  setLoadingList(true)

  if (timeDelay) await delay(timeDelay)

  const q = query(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_SUPPLIER,
    ),
    ...(whereList || []),
    ...(orderByList || []),
    ...(startAfter || []),
    ...(limit || []),
  )

  onSnapshot(
    q,
    querySnapshot => {
      const supplierList: Supplier[] = []
      querySnapshot.forEach(doc => {
        const dataItem = { id: doc.id, ...doc.data() } as Supplier
        supplierList.push(dataItem)
      })
      setLoadingList(false)
      setSupplierList(supplierList)
    },
    () => {
      console.log('Supplier - getList() - error')
      setLoadingList(false)
    },
  )
}

interface GetOneProps {
  companyId: string
  supplierId: string
}
export const getOne = async ({
  companyId,
  supplierId,
}: GetOneProps): Promise<Supplier | undefined> => {
  const docRef = doc(
    firestore,
    FirebaseCollection.BASE_COMPANY,
    companyId,
    FirebaseCollection.BASE_SUPPLIER,
    supplierId,
  )
  const docSnap = await getDoc(docRef)

  try {
    if (docSnap.exists()) {
      return {
        id: supplierId,
        ...docSnap.data(),
      } as Supplier
    }
  } catch (error) {
    return undefined
  }

  return undefined
}

interface SaveProps {
  companyId: string
  values: Partial<Supplier>
}
export const save = async ({ companyId, values }: SaveProps) =>
  await addDoc(
    collection(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_SUPPLIER,
    ),
    {
      ...values,
      nameSearch: `${(values.name || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      emailSearch: `${(values.email || '')
        .trimEnd()
        .trimStart()}${new Date().getTime()}`.toUpperCase(),
      createdAt: new Date().getTime(),
      dateCreatedAt: new Date().setHours(0, 0, 0, 0),
      updatedAt: new Date().getTime(),
    },
  ).then(resp => resp.id)

interface updateProps extends FullUpdate {
  companyId: string
  supplierId: string
  values: Partial<Supplier>
}
export const update = async ({
  companyId,
  supplierId,
  values,
  isFullUpdate = true,
}: updateProps) => {
  await setDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_SUPPLIER,
      supplierId,
    ),
    {
      ...values,
      ...(isFullUpdate && {
        nameSearch: `${(values.name || '')
          .trimEnd()
          .trimStart()}${new Date().getTime()}`.toUpperCase(),
        emailSearch: `${(values.email || '')
          .trimEnd()
          .trimStart()}${new Date().getTime()}`.toUpperCase(),
      }),
      updatedAt: new Date().getTime(),
    },
    { merge: true },
  )
}

interface RemoveProps {
  companyId: string
  supplierId: string
}
export const remove = async ({ companyId, supplierId }: RemoveProps) => {
  await deleteDoc(
    doc(
      firestore,
      FirebaseCollection.BASE_COMPANY,
      companyId,
      FirebaseCollection.BASE_SUPPLIER,
      supplierId,
    ),
  )
}
